import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Users, MessageCircle, Heart, Share2, TrendingUp } from 'lucide-react';
import { mockCommunityPosts } from '../mock';
import { Badge } from '../components/ui/badge';
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';

const Community = () => {
  const [posts, setPosts] = useState(mockCommunityPosts);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState('');

  const handleCreatePost = () => {
    if (!newPost.trim()) {
      toast({ title: "Error", description: "Please write something", variant: "destructive" });
      return;
    }

    const post = {
      post_id: `post_${Date.now()}`,
      user_name: 'Sarah Johnson',
      user_avatar: 'SJ',
      date: new Date(),
      content: newPost,
      likes: 0,
      comments: 0,
      category: 'General'
    };

    setPosts([post, ...posts]);
    setNewPost('');
    setDialogOpen(false);
    toast({ title: "Success!", description: "Post created successfully" });
  };

  const categoryColors = {
    Milestone: 'bg-green-100 text-green-700',
    Question: 'bg-blue-100 text-blue-700',
    Recipe: 'bg-orange-100 text-orange-700',
    Success: 'bg-purple-100 text-purple-700',
    General: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Users className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-3xl font-bold">Community</h2>
            <p className="text-indigo-100 text-lg">Connect with others on their diabetes journey</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Members</CardTitle>
            <Users className="w-5 h-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">12,547</div>
            <p className="text-sm text-emerald-600 mt-1">+234 this week</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Posts</CardTitle>
            <MessageCircle className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">3,892</div>
            <p className="text-sm text-gray-600 mt-1">This month</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Today</CardTitle>
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">1,245</div>
            <p className="text-sm text-gray-600 mt-1">Online now: 347</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Success Stories</CardTitle>
            <Heart className="w-5 h-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">567</div>
            <p className="text-sm text-gray-600 mt-1">Shared</p>
          </CardContent>
        </Card>
      </div>

      {/* Create Post */}
      <Card className="shadow-md border-0">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              SJ
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button className="flex-1 text-left px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-600">
                  Share your thoughts, ask a question...
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create a Post</DialogTitle>
                  <DialogDescription>Share with the community</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <Textarea
                    placeholder="What's on your mind?"
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    rows={6}
                  />
                  <Button onClick={handleCreatePost} className="w-full bg-gradient-to-r from-indigo-500 to-purple-500">
                    Post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Posts Feed */}
      <div className="space-y-4">
        {posts.map((post) => (
          <Card key={post.post_id} className="shadow-md border-0 hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {post.user_avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{post.user_name}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                      {' at '}
                      {new Date(post.date).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <Badge className={categoryColors[post.category]}>
                  {post.category}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700 leading-relaxed">{post.content}</p>
              <div className="flex items-center space-x-6 pt-2 border-t">
                <button className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm font-medium">{post.likes}</span>
                </button>
                <button className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{post.comments}</span>
                </button>
                <button className="flex items-center space-x-2 text-gray-600 hover:text-emerald-600 transition-colors">
                  <Share2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Share</span>
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Community;
